import React from "react";
import { Modal, Tabs, Space, Button, Descriptions, Tag, Divider, Card, Row, Col, Table, Tooltip, Spin } from "antd";
import {
  ShoppingCartOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  FileTextOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import dateFormat from "dateformat";

const { TabPane } = Tabs;

const OrderDetailsModal = ({
  visible,
  selectedOrder,
  activeTab,
  onTabChange,
  onCancel,
  onEditItems,
  onConfirmOrder,
  onRejectOrder,
  onRevertOrder,
  confirmLoading,
  customerSummary,
  loadingCustomerSummary,
  renderCustomerSummary,
  OrderStatusTab,
  handleUpdateOrderStatus,
  onSendSms,
  onSendStockOutSms,
  onSendAlreadyCalledSms,
  loading,
}) => {
  const formatter = new Intl.NumberFormat("en-IN");

  if (!selectedOrder) return null;

  const orderData = selectedOrder.data || {};
  const contact = selectedOrder.contact || orderData.contact || {};
  const items = selectedOrder.items || orderData.items || [];

  const handleConfirmClick = () => {
    // Check if any item's warehouse is not 'Main Warehouse'
    const invalidItems = items.filter((item) => {
      const warehouse =
        item.warehouse ||
        (item.product_location && item.product_location.warehouse);
      const warehouseName =
        (warehouse && warehouse.name) || item.warehouse_name || "";
      return warehouseName.trim().toLowerCase() !== "main warehouse";
    });

    if (invalidItems.length > 0) {
      Modal.warning({
        title: "Item does not exist in Main Warehouse",
        width: 600,
        content: (
          <div>
            <p style={{ marginBottom: 12 }}>
              The order cannot be confirmed because the following item(s) are not assigned to <strong>Main Warehouse</strong>:
            </p>
            <div
              style={{
                background: "#fffbe6",
                border: "1px solid #ffe58f",
                borderRadius: 4,
                padding: 12,
                maxHeight: "250px",
                overflowY: "auto",
              }}
            >
              {invalidItems.map((item, idx) => {
                const warehouse =
                  item.warehouse ||
                  (item.product_location && item.product_location.warehouse);
                const warehouseName =
                  (warehouse && warehouse.name) || item.warehouse_name || "N/A";
                const productLocationId =
                  item.product_location && item.product_location.id;
                const productName =
                  item.product_name ||
                  (item.product_details && item.product_details.name) ||
                  (item.product_details && item.product_details.title) ||
                  `Product #${item.product || productLocationId || idx + 1}`;

                return (
                  <div
                    key={idx}
                    style={{
                      padding: "6px 0",
                      borderBottom:
                        idx !== invalidItems.length - 1
                          ? "1px solid #f0f0f0"
                          : "none",
                    }}
                  >
                    <div style={{ fontWeight: 600, color: "#333" }}>
                      {productName}
                    </div>
                    <div style={{ fontSize: 12, color: "#fa8c16" }}>
                      Current Warehouse: <strong>{warehouseName}</strong> (Expected: Main Warehouse)
                    </div>
                  </div>
                );
              })}
            </div>
            <p style={{ marginTop: 12, marginBottom: 0, color: "#666" }}>
              Please click <strong>Edit Items</strong> to select products from <strong>Main Warehouse</strong> before confirming this order.
            </p>
          </div>
        ),
      });
      return;
    }

    onConfirmOrder(selectedOrder.id);
  };

  return (
    <Modal
      title={
        <Space>
          <ShoppingCartOutlined />
          Order Details - {orderData.order_number || selectedOrder.invoice_number}
        </Space>
      }
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={1400}
      style={{ top: 20 }}
    >
      
      <Tabs
        activeKey={activeTab || "order"}
        onChange={onTabChange}
        tabBarExtraContent={
          <Space>
            {!selectedOrder.confirm && orderData.status !== "Ecommerce Rejected" && (
              <>
                <Button type="default" icon={<EditOutlined />} onClick={onEditItems}>
                  Edit Items
                </Button>
                <Button type="primary" onClick={onSendSms} style={{ backgroundColor: "#000000ff" }}>
                  Payment SMS
                </Button>
                <Button type="primary" onClick={onSendStockOutSms} style={{ backgroundColor: "#ff4d4f" }}>
                  Stock Out SMS
                </Button>
                <Button type="primary" onClick={onSendAlreadyCalledSms} style={{ backgroundColor: "#1890ff" }}>
                  Already Called SMS
                </Button>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  onClick={handleConfirmClick}
                  style={{ backgroundColor: "green" }}
                >
                  Confirm Order
                </Button>
                <Button danger icon={<CloseCircleOutlined />} onClick={() => onRejectOrder(selectedOrder.id)}>
                  Reject Order
                </Button>
              </>
            )}
            {orderData.status === "Ecommerce Rejected" && (
              <Button
                type="primary"
                icon={<HistoryOutlined />}
                onClick={() => onRevertOrder(selectedOrder.id)}
                style={{ backgroundColor: "#faad14" }}
              >
                Revert
              </Button>
            )}
          </Space>
        }
      >
        <TabPane tab={<span><ShoppingCartOutlined /> Order Details</span>} key="order">
          <Spin spinning={confirmLoading}>
            {/* Order Information */}
            <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Order Number" span={1}>
                <strong>{orderData.order_number || selectedOrder.invoice_number}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Invoice Number" span={1}>
                {selectedOrder.invoice_number || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Status" span={1}>
                <Tag color={orderData.status === "Ecommerce Pending" ? "orange" : "green"}>
                  {orderData.status || "Ecommerce Pending"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Confirmed" span={1}>
                <Tag color={selectedOrder.confirm ? "green" : "red"}>
                  {selectedOrder.confirm ? "Yes" : "No"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="OTP Verified" span={1}>
                <Tag color={selectedOrder.otp_verified ? "green" : "red"}>
                  {selectedOrder.otp_verified ? "Yes" : "No"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Order Status" span={1}>
                {selectedOrder.order_status || "N/A"}
              </Descriptions.Item>
              <Descriptions.Item label="Order Date" span={1}>
                {dateFormat(selectedOrder.created_at, "dd-mm-yyyy hh:MM TT")}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated" span={1}>
                {dateFormat(selectedOrder.updated_at, "dd-mm-yyyy hh:MM TT")}
              </Descriptions.Item>
            </Descriptions>

            {/* Customer Information */}
            <Divider orientation="left">Customer Information</Divider>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Name" span={1}>{contact.name || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Phone" span={1}>{contact.phone || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Email" span={2}>{contact.email || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Address" span={2}>{contact.address || "N/A"}</Descriptions.Item>
              <Descriptions.Item label="Shipping Name" span={1}>
                {orderData.shipping_name || "Same as above"}
              </Descriptions.Item>
              <Descriptions.Item label="Shipping Phone" span={1}>
                {orderData.shipping_phone || "Same as above"}
              </Descriptions.Item>
              <Descriptions.Item label="Shipping Address" span={2}>
                {orderData.shipping_address || "Same as above"}
              </Descriptions.Item>
            </Descriptions>

            {/* Order Items Table */}
            <Divider orientation="left">Order Items</Divider>
            <Table
              dataSource={items}
              pagination={false}
              rowKey={(record, index) => index}
              columns={[
                {
                  title: "SL",
                  key: "sl",
                  render: (text, record, index) => index + 1,
                },
                {
                  title: "Product Name",
                  key: "product_name",
                  render: (_, record) => {
                    const productDetails = record.product_details;
                    if (productDetails) {
                      return (
                        <div>
                          <div style={{ fontWeight: "bold" }}>
                            {productDetails.name || productDetails.title || "N/A"}
                          </div>
                          {productDetails.description && (
                            <div style={{ fontSize: "12px", color: "#888" }}>
                              {productDetails.description}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return record.product_name || `Product #${record.product}` || "N/A";
                  },
                },
                {
                  title: "Product Code",
                  key: "product_code",
                  render: (_, record) => {
                    const productDetails = record.product_details;
                    return (productDetails && productDetails.title) || "N/A";
                  },
                },
                {
                  title: "Barcode",
                  key: "barcode",
                  render: (_, record) => {
                    const productLocation = record.product_location;
                    return productLocation && productLocation.barcode ? (
                      <Tooltip title={productLocation.barcode}>
                        <Space>{productLocation.barcode}</Space>
                      </Tooltip>
                    ) : "N/A";
                  },
                },
                {
                  title: "Attributes",
                  key: "attributes",
                  render: (_, record) => {
                    const productLocation = record.product_location || {};
                    const attributes = productLocation.attributes || productLocation.Attributes || record.attributes;

                    const hasValidAttributeObjects =
                      Array.isArray(attributes) &&
                      attributes.length > 0 &&
                      typeof attributes[0] === "object" &&
                      attributes[0] !== null &&
                      (attributes[0].term_name || attributes[0].name);

                    if (hasValidAttributeObjects) {
                      return (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                          {attributes.map((attr, index) => {
                            const termName = attr.term_name || attr.name || (attr.attribute && attr.attribute.name);
                            const title = attr.attribute
                              ? `${attr.attribute.name || ""}: ${termName}`
                              : termName;
                            return (
                              <Tooltip key={index} title={title}>
                                <span>{termName}</span>
                              </Tooltip>
                            );
                          })}
                        </div>
                      );
                    }

                    const attributeDetails =
                      record.Attribute_details ||
                      record.size ||
                      productLocation.Attribute_details ||
                      productLocation.attribute_details ||
                      productLocation.size ||
                      (record.product_details && record.product_details.Attribute_details);

                    return attributeDetails || "N/A";
                  },
                },
                {
                  title: "Warehouse",
                  key: "warehouse",
                  render: (_, record) => {
                    const warehouse = record.warehouse;
                    return warehouse ? (
                      <Tooltip title={`${warehouse.address || ""} ${warehouse.contact || ""}`}>
                        <Space>{warehouse.name}</Space>
                      </Tooltip>
                    ) : "N/A";
                  },
                },
                {
                  title: "Order Qty",
                  dataIndex: "quantity",
                  key: "quantity",
                  align: "center",
                  render: (qty) => <strong>{qty}</strong>,
                },
                {
                  title: "Unit Price",
                  dataIndex: "price",
                  key: "price",
                  align: "right",
                  render: (price) => `${formatter.format(price)}`,
                },
                {
                  title: "Discount",
                  dataIndex: "spec_discount",
                  key: "spec_discount",
                  align: "right",
                  render: (discount) => `${formatter.format(discount || 0)}`,
                },
                {
                  title: "Total",
                  key: "total",
                  align: "right",
                  render: (_, record) => (
                    <strong>
                      {formatter.format(record.quantity * record.price - (record.spec_discount || 0))}
                    </strong>
                  ),
                },
              ]}
              summary={(pageData) => {
                let totalQuantity = 0;
                let totalAmount = 0;
                let totalDiscount = 0;

                pageData.forEach(({ quantity, price, spec_discount }) => {
                  totalQuantity += quantity;
                  totalAmount += quantity * price;
                  totalDiscount += spec_discount || 0;
                });

                return (
                  <Table.Summary fixed>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={6} align="right">
                        <strong>TOTAL:</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center">
                        <strong>{totalQuantity}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <strong>{formatter.format(totalAmount)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <strong>{formatter.format(totalDiscount)}</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <strong>{formatter.format(totalAmount - totalDiscount)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={6} align="right">
                        <strong>DELIVERY CHARGE:</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center" />
                      <Table.Summary.Cell index={2} align="right">
                        <div style={{ fontSize: '14px', color: '#030303ff' }}>
                          {orderData.delivery_charge === 80 && "Inside Dhaka City"}
                          {orderData.delivery_charge === 100 && "Inside Dhaka Sub Area"}
                          {orderData.delivery_charge === 150 && "Outside Dhaka"}
                        </div>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right" />
                      <Table.Summary.Cell index={4} align="right">
                        <strong>{formatter.format(orderData.delivery_charge || 0)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={6} align="right">
                        <strong>TOTAL BILL:</strong>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="center" />
                      <Table.Summary.Cell index={2} align="right" />
                      <Table.Summary.Cell index={3} align="right" />
                      <Table.Summary.Cell index={4} align="right">
                        <strong>{formatter.format(orderData.bill || 0)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />

            {/* Customer Notes */}
            {orderData.notes && (
              <>
                <Divider orientation="left">Customer Notes</Divider>
                <Card size="small">
                  <p style={{ margin: 0, whiteSpace: "pre-wrap", color: "red", fontSize: "18px" }}>
                    {orderData.notes}
                  </p>
                </Card>
              </>
            )}

            {/* Payment Information */}
            <Divider orientation="left">Payment Information</Divider>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card title="Order Summary" bordered size="small">
                  <Descriptions column={1}>
                    <Descriptions.Item label="Subtotal">
                      {formatter.format((orderData.bill || 0) - (orderData.delivery_charge || 0))}
                    </Descriptions.Item>
                    <Descriptions.Item label="Delivery Charge">
                      {formatter.format(orderData.delivery_charge || 0)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Discount">
                      {formatter.format(orderData.discount || 0)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tax">
                      {formatter.format(orderData.tax || 0)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Bill">
                      <strong style={{ fontSize: "18px", color: "#1890ff" }}>
                        {formatter.format(orderData.bill || 0)}
                      </strong>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Payment Details" bordered size="small">
                  <Descriptions column={1}>
                    <Descriptions.Item label="Payment Method">
                      <Tag color="blue">{orderData.Payment_method || "N/A"}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Account No">
                      {orderData.Account_no || "N/A"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Transaction No">
                      {orderData.Transection_no || "N/A"}
                    </Descriptions.Item>
                    <Descriptions.Item label="Advance Payment">
                      <Tag color="green">{formatter.format(orderData.advance_payment || 0)}</Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="Due Amount">
                      <strong style={{ fontSize: "18px", color: "#ff4d4f" }}>
                        {formatter.format(orderData.due || 0)}
                      </strong>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            </Row>
          </Spin>
        </TabPane>

        <TabPane tab={<span><FileTextOutlined /> Order Status</span>} key="status">
          <OrderStatusTab
            selectedOrder={selectedOrder}
            onUpdate={handleUpdateOrderStatus}
            loading={loading}
          />
        </TabPane>

        <TabPane
          tab={
            <span>
              <HistoryOutlined /> Customer History
              {loadingCustomerSummary && <Spin size="small" style={{ marginLeft: 8 }} />}
            </span>
          }
          key="customer"
        >
          {renderCustomerSummary()}
        </TabPane>
      </Tabs>
    </Modal>
  );
};

export default OrderDetailsModal;